import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

/**
 * Shared modal shell for the Estimates / Invoices / Closed Orders pages.
 *
 * Layout: pinned header -> scrolling body -> pinned footer, capped at 92vh on
 * desktop and full-screen on phones. Body scroll locking is still handled by
 * each page via useModalScrollLock.
 *
 * Sizes:
 *   sm   448px   confirmations (delete, payment status, cancel)
 *   md   672px   pickers (select an estimate / closed order)
 *   lg   1024px  edit estimate / invoice / closed order forms
 *   xl   1024px  conversion modals
 *   full 1152px  edit products grid
 *
 * Layers: base (z-50), stacked (z-[60], a modal opened on top of another),
 * top (z-[70], confirmations that must sit above everything).
 */
const SIZE_CLASSES = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-2xl',
  lg: 'sm:max-w-5xl',
  xl: 'sm:max-w-5xl',
  full: 'sm:max-w-6xl',
};

const LAYER_CLASSES = {
  base: 'z-50',
  stacked: 'z-[60]',
  top: 'z-[70]',
};

// Many-button footers (view modals): smaller buttons kept on one row on desktop.
const COMPACT_FOOTER = 'flex-wrap lg:flex-nowrap gap-2 [&_button]:px-3 [&_button]:py-2 [&_button]:text-sm [&_button]:whitespace-nowrap [&>div]:px-3 [&>div]:py-2 [&>div]:text-sm';

const openModalStack = [];

const AppModal = ({
  open = true,
  onClose,
  title,
  subtitle,
  icon,
  headerActions,
  footer,
  size = 'lg',
  layer = 'base',
  closeOnEsc = true,
  bodyClassName = '',
  compactFooter = false,
  className = '',
  children,
}) => {
  const onCloseRef = useRef(onClose);
  const closeOnEscRef = useRef(closeOnEsc);
  onCloseRef.current = onClose;
  closeOnEscRef.current = closeOnEsc;

  // Escape closes only the topmost open modal, so a modal stacked over
  // another (e.g. Edit Products over a convert modal) doesn't close both.
  useEffect(() => {
    if (!open) return undefined;
    const token = {};
    openModalStack.push(token);
    const handleKey = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (openModalStack[openModalStack.length - 1] !== token) return;
      if (closeOnEscRef.current && onCloseRef.current) onCloseRef.current();
    };
    window.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('keydown', handleKey);
      const i = openModalStack.indexOf(token);
      if (i !== -1) openModalStack.splice(i, 1);
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className={`app-modal-overlay fixed inset-0 ${LAYER_CLASSES[layer] || LAYER_CLASSES.base} flex items-stretch sm:items-center justify-center bg-slate-900/60 backdrop-blur-sm sm:p-4`}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={`relative flex flex-col w-full ${SIZE_CLASSES[size] || SIZE_CLASSES.lg} h-full sm:h-auto sm:max-h-[min(85vh,100%)] bg-[#F8F5E6] sm:rounded-2xl shadow-2xl overflow-hidden ${className}`}
      >
        {(title || onClose || headerActions) && (
          <div className="flex items-start justify-between gap-4 px-4 sm:px-6 py-3 sm:py-4 bg-white border-b border-[#E6DFC6] flex-shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              {icon && (
                <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-[#0A5F38]/10 text-[#0A5F38] flex-shrink-0">
                  {icon}
                </div>
              )}
              <div className="min-w-0">
                {title && (
                  <h2 className="text-lg sm:text-xl font-bold text-[#0A5F38] truncate">{title}</h2>
                )}
                {subtitle && (
                  <p className="text-xs sm:text-sm text-slate-500 truncate">{subtitle}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {headerActions}
              {onClose && (
                <button
                  type="button"
                  onClick={onClose}
                  className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        )}

        <div className={`flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-4 sm:py-5 scrollbar-thin ${bodyClassName}`}>
          {children}
        </div>

        {footer && (
          <div className={`flex items-center justify-end px-4 sm:px-6 py-3 sm:py-4 bg-white border-t border-[#E6DFC6] flex-shrink-0 ${compactFooter ? COMPACT_FOOTER : 'flex-wrap gap-3'}`}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Shared class names for product line-item tables inside modals.
 * Column order everywhere: Category, Product, Units, Qty, Price, then
 * Disc %, Amount, GST %, GST Amt, Total, Actions.
 */
export const modalTable = {
  wrap: 'bg-white border border-[#E6DFC6] rounded-xl overflow-auto max-h-[60vh]',
  table: 'w-full min-w-[900px] text-sm border-collapse',
  thead: 'bg-[#0A5F38] text-white sticky top-0 z-10',
  th: 'px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap',
  thCenter: 'px-3 py-2.5 text-center text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap',
  thRight: 'px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap',
  tbody: 'divide-y divide-slate-100',
  tr: 'hover:bg-[#F8F5E6]/60 transition-colors',
  td: 'px-3 py-2 text-slate-800 align-middle',
  tdCenter: 'px-3 py-2 text-center text-slate-800 align-middle',
  tdRight: 'px-3 py-2 text-right text-slate-800 align-middle whitespace-nowrap',
  tfoot: 'bg-[#F8F5E6] font-semibold',
  input: 'w-full px-2 py-1.5 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0A5F38]/30 focus:border-[#0A5F38]',
};

/** Shared button class names for modal footers. */
export const modalButton = {
  primary: 'inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-[#0A5F38] text-white font-semibold hover:bg-[#084d2d] disabled:opacity-50 disabled:cursor-not-allowed transition-colors',
  secondary: 'inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-white text-slate-700 font-semibold border border-slate-300 hover:bg-slate-50 transition-colors',
  danger: 'inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-50 transition-colors',
};

export default AppModal;
